import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MapPin, Phone, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PanditPage() {
  const navigate = useNavigate();
  const [pandits, setPandits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLocation, setSearchLocation] = useState('');

  useEffect(() => {
    fetchPandits();
  }, []);

  const fetchPandits = async (location = '') => {
    try {
      const url = location ? `${API}/pandit/profiles?location=${location}` : `${API}/pandit/profiles`;
      const response = await axios.get(url);
      setPandits(response.data);
    } catch (error) {
      toast.error('Failed to load pandits');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchPandits(searchLocation);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-4xl text-[#FF9933] mb-2">ॐ</div>
          <p className="text-slate-600">Loading pandits...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="text-center mb-8 peaceful-enter">
        <h2 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
          Find Pandit Ji
        </h2>
        <p className="text-slate-600">Connect with verified pandits for your spiritual needs</p>
      </div>

      {/* Search */}
      <div className="mb-8 bg-white rounded-2xl shadow-sm border border-stone-100 p-6 peaceful-enter">
        <div className="flex gap-3">
          <Input
            data-testid="search-location-input"
            placeholder="Search by location..."
            value={searchLocation}
            onChange={(e) => setSearchLocation(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="rounded-xl border-stone-200"
          />
          <Button
            data-testid="search-btn"
            onClick={handleSearch}
            className="rounded-full bg-[#FF9933] hover:bg-[#E68A00] px-6"
          >
            Search
          </Button>
        </div>
      </div>

      {/* Pandit List */}
      <div className="space-y-6">
        {pandits.map((pandit, index) => (
          <div
            key={pandit.id}
            data-testid={`pandit-card-${index}`}
            className="bg-white rounded-2xl p-6 border-l-4 border-orange-400 shadow-sm hover:shadow-md transition-shadow peaceful-enter"
            style={{ animationDelay: `${index * 0.05}s` }}
          >
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
              <img
                src={pandit.avatar || 'https://images.unsplash.com/photo-1762838105804-c3264c29a390?w=200&h=200&fit=crop'}
                alt={pandit.name}
                className="w-20 h-20 rounded-full object-cover border-2 border-orange-100"
              />

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Merriweather, serif' }}>
                    {pandit.name}
                  </h3>
                  {pandit.verified && (
                    <CheckCircle className="w-5 h-5 text-[#FF9933]" fill="#FF9933" />
                  )}
                </div>

                <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-3">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{pandit.location}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    <span>{pandit.phone}</span>
                  </div>
                  <div>
                    <span className="font-medium">{pandit.experience_years} years experience</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-3">
                  {pandit.puja_types.slice(0, 3).map((puja, i) => (
                    <span key={i} className="px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium">
                      {puja}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-lg font-semibold text-[#FF9933]">₹{pandit.fees_per_puja} per puja</p>
                  <Button
                    data-testid={`view-pandit-btn-${index}`}
                    onClick={() => navigate(`/pandit/${pandit.id}`)}
                    className="rounded-full bg-[#FF9933] hover:bg-[#E68A00]"
                  >
                    View Profile
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {pandits.length === 0 && (
          <div className="text-center py-12 bg-white rounded-2xl">
            <p className="text-slate-600">No pandits found. Try a different location.</p>
          </div>
        )}
      </div>
    </div>
  );
}
